<template>
  <div id="dish-form">
    <div class="mb-3">
      <v-btn
        text
        color="primary"
        class="dish-form__back text-none"
        :to="backTo"
        exact
      >
        <v-icon
          size="18"
          class="me-1"
        >
          {{ icons.mdiArrowLeft }}
        </v-icon>
        <span>{{ backText }}</span>
      </v-btn>
    </div>

    <v-card>
      <div
        v-if="loading"
        class="d-flex justify-center py-16"
      >
        <v-progress-circular
          :size="48"
          :width="4"
          color="primary"
          indeterminate
        ></v-progress-circular>
      </div>

      <v-card-text
        v-else-if="failed"
        class="text-center py-12 text--secondary"
      >
        <div class="mb-4">
          Не удалось загрузить блюдо
        </div>
        <v-btn
          color="primary"
          outlined
          @click="fetchById($route.params.id)"
        >
          Повторить
        </v-btn>
      </v-card-text>

      <template v-else>
        <v-row no-gutters>
          <v-col
            cols="12"
            md="5"
          >
            <div class="dish-form__photo pa-6">
              <dish-photo
                v-model="image"
                :alt="name"
                @formData-updated="imageUploaded"
                @file-removed="imageRemoved"
              ></dish-photo>

              <v-divider class="my-4"></v-divider>
              <div class="d-flex align-center flex-wrap">
                <v-btn
                  color="primary"
                  class="me-3 mb-2"
                  :disabled="!photoChanged || hasNotPermission('Permissions.DishUpdateImage')"
                  @click="saveData"
                >
                  Сохранить фото
                </v-btn>
                <v-btn
                  color="secondary"
                  outlined
                  class="mb-2"
                  @click="cancel"
                >
                  Отмена
                </v-btn>
              </div>
            </div>
          </v-col>

          <v-col
            cols="12"
            md="7"
          >
            <div class="pa-6 ps-md-2">
              <div
                v-if="marks.length"
                class="mb-2"
              >
                <v-chip
                  v-for="mark in marks"
                  :key="mark.key"
                  :color="mark.color"
                  x-small
                  outlined
                  class="me-1"
                >
                  {{ mark.text }}
                </v-chip>
              </div>

              <h1 class="dish-form__name text-h5 text--primary">
                {{ name }}
              </h1>
              <p
                v-if="recipe"
                class="text-body-2 text--secondary mt-2 mb-0"
              >
                {{ recipe }}
              </p>

              <div class="dish-form__figures d-flex flex-wrap mt-6">
                <div>
                  <div class="text-caption text--secondary">
                    Цена
                  </div>
                  <div class="dish-form__figure text-h5 text--primary">
                    {{ toMoney(price) }}
                  </div>
                </div>

                <div>
                  <div class="text-caption text--secondary">
                    Вес
                  </div>
                  <div class="dish-form__figure text-h5 text--primary">
                    {{ toWeight(weight) }}
                  </div>
                </div>
              </div>

              <template v-if="isCombo">
                <div class="text-overline text--secondary mt-8">
                  Состав комбо<span v-if="child.length"> · {{ toDishCount(child.length) }}</span>
                </div>
                <v-divider></v-divider>

                <div
                  v-if="!child.length"
                  class="text-body-2 text--secondary py-4"
                >
                  {{ childLoading ? 'Загружаем состав…' : 'В наборе нет блюд' }}
                </div>

                <template v-for="item in child">
                  <div
                    :key="item.id"
                    class="dish-form__child"
                    tabindex="0"
                    @click="openDish(item.id)"
                    @keydown.enter="openDish(item.id)"
                  >
                    <dish-image
                      :src="item.image"
                      :size="40"
                    ></dish-image>
                    <div class="dish-form__child-text mx-3">
                      <div
                        class="text-body-2 font-weight-medium text--primary text-truncate"
                        :title="item.name"
                      >
                        {{ item.name }}
                      </div>
                      <div class="text-caption text--secondary text-truncate">
                        {{ toWeight(item.weight) }}
                      </div>
                    </div>
                    <div class="dish-form__figure text-body-2 font-weight-medium text--primary text-no-wrap">
                      {{ toMoney(item.price) }}
                    </div>
                  </div>
                  <v-divider :key="`divider-${item.id}`"></v-divider>
                </template>
              </template>

              <div class="text-overline text--secondary mt-8">
                Модификаторы
              </div>
              <v-divider></v-divider>

              <div
                v-if="!schemes.length"
                class="text-body-2 text--secondary py-4"
              >
                У блюда нет модификаторов
              </div>

              <div
                v-for="scheme in schemes"
                :key="scheme.name"
                class="mt-4"
              >
                <div class="text-subtitle-2 text--primary">
                  {{ scheme.name || 'Схема без названия' }}
                </div>

                <div
                  v-for="(group, index) in scheme.groups"
                  :key="index"
                  class="dish-form__group mt-3 pa-4"
                >
                  <div class="d-flex align-baseline flex-wrap">
                    <span class="text-body-2 font-weight-medium text--primary me-3">Группа {{ index + 1 }}</span>
                    <span class="text-caption text--secondary">{{ group.rules }}</span>
                  </div>

                  <div
                    v-if="!group.parts.length"
                    class="text-caption text--secondary mt-2"
                  >
                    В группе нет модификаторов
                  </div>

                  <div
                    v-for="part in group.parts"
                    :key="part.type || 'plain'"
                    class="mt-2"
                  >
                    <div
                      v-if="part.title"
                      class="text-caption text--secondary mb-1"
                    >
                      {{ part.title }}
                    </div>
                    <v-chip
                      v-for="modifier in part.modifiers"
                      :key="modifier.id"
                      small
                      outlined
                      class="me-1 mb-1"
                    >
                      {{ modifier.name }}
                      <span
                        v-if="modifier.price"
                        class="dish-form__figure font-weight-medium text--primary ms-2"
                      >{{ toMoney(modifier.price) }}</span>
                    </v-chip>
                  </div>
                </div>
              </div>
            </div>
          </v-col>
        </v-row>
      </template>
    </v-card>
  </div>
</template>

<script>
import { mdiArrowLeft } from '@mdi/js'
import { mapActions, mapGetters, mapState } from 'vuex'
import { mapFormHandlers } from '@/helpers/forms'
import DishImage from './dish-image.vue'
import DishPhoto from './dish-photo.vue'
import { hasPermission } from '@/helpers'
import {
  comboTypeText, groupModifiersByType, toDishCount, toModifierRules, toMoney, toWeight,
} from '@/helpers/menu'

export default {
  name: 'DishForm',
  components: { DishImage, DishPhoto },
  data: () => ({
    icons: {
      mdiArrowLeft,
    },
    newImage: null,
    isImageDeleted: false,

    savedImage: '',
    childLoading: false,
  }),
  computed: {
    ...mapState('dish/form', {
      schemas: 'schemas',
      loading: 'loading',
      failed: 'failed',
      comboType: 'comboType',
    }),
    ...mapState('menuCategory', {
      menuCategories: 'list',
    }),
    ...mapGetters('dish/form', { child: 'child' }),
    ...mapFormHandlers('dish/form', [
      'name',
      'price',
      'isCombo',
      'recipe',
      'weight',
      'image',
    ]),

    photoChanged() {
      return !!this.newImage || this.isImageDeleted
    },

    marks() {
      const marks = []

      if (this.isCombo) marks.push({ key: 'combo', text: `комбо · ${comboTypeText(this.comboType)}`, color: 'info' })
      if (!this.image) marks.push({ key: 'no-photo', text: 'нет фото', color: 'warning' })

      return marks
    },

    schemes() {
      return (this.schemas || []).filter(x => !x.isComboCollector).reduce((schemes, schema) => {
        let scheme = schemes.find(x => x.name === schema.name)

        if (!scheme) {
          scheme = { name: schema.name, groups: [] }
          schemes.push(scheme)
        }

        scheme.groups.push({ rules: toModifierRules(schema), parts: groupModifiersByType(schema.modifiers) })

        return schemes
      }, [])
    },

    backTo() {
      const { menuVersionId, category } = this.$route.query

      if (!menuVersionId) return { name: 'menu-version' }

      return {
        name: 'menu-version-menu-category-list',
        params: { menuVersionId },
        query: category ? { category } : {},
      }
    },
    backText() {
      const { menuVersionId, category } = this.$route.query

      if (!menuVersionId) return 'Меню'

      const found = this.menuCategories.find(x => x.id === Number(category))

      return found ? found.name : 'К списку блюд'
    },
  },
  watch: {
    loading(newValue) {
      if (newValue) return

      this.savedImage = this.image || ''
      this.newImage = null
      this.isImageDeleted = false
    },
    isCombo(newValue) {
      if (!newValue) return

      const schema = (this.schemas || []).find(x => x.isComboCollector)

      if (!schema || schema.modifierGroupDetailId === null) return

      const done = () => {
        this.childLoading = false
      }

      this.childLoading = true
      this.getChild(schema.modifierGroupDetailId).then(done, done)
    },
    '$route.params.id': {
      handler(newValue, oldValue) {
        if (oldValue !== undefined) {
          const { id } = this.$route.params
          this.fetchById(id)
        }
      },
      deep: true,
      immediate: true,
    },
  },
  created() {
    this.fetchById(this.$route.params.id)
  },
  methods: {
    toMoney,
    toWeight,
    toDishCount,
    hasNotPermission(value) {
      return !hasPermission(value)
    },
    imageUploaded(blob) {
      this.newImage = blob
      this.isImageDeleted = false
    },

    imageRemoved() {
      this.newImage = null
      this.isImageDeleted = !!this.savedImage
    },

    openDish(id) {
      this.$router.push({ name: 'menu-version-dish-form', params: { id }, query: this.$route.query })
    },
    cancel() {
      this.resetForm()
      this.$router.back()
    },
    ...mapActions('dish/form', ['fetchById', 'uploadPhoto', 'getChild', 'removePhoto', 'resetForm']),
    async saveData() {
      const { id } = this.$route.params
      if (this.isImageDeleted) {
        await this.removePhoto(id)
      }
      if (this.newImage) {
        const form = new FormData()
        form.append('file', this.newImage)
        form.append('dishId', id)
        await this.uploadPhoto(form)
      }
      this.$router.back()
    },
  },
}
</script>

<style lang="scss" scoped>
.dish-form__back {
  margin-left: -8px;
}

.dish-form__photo {
  @media (min-width: 960px) {
    position: sticky;
    top: 80px;
  }
}

.dish-form__name {
  word-break: break-word;
}

.dish-form__figures {
  gap: 12px 48px;
}

.dish-form__figure {
  font-variant-numeric: tabular-nums;
}

.dish-form__child {
  display: flex;
  align-items: center;
  padding: 8px 0;
  cursor: pointer;

  &:hover,
  &:focus-visible {
    background: rgba(0, 0, 0, 0.04);
  }

  &:focus-visible {
    outline: 2px solid var(--v-primary-base);
    outline-offset: -2px;
  }
}

.theme--dark .dish-form__child {
  &:hover,
  &:focus-visible {
    background: rgba(255, 255, 255, 0.06);
  }
}

.dish-form__child-text {
  flex: 1;
  min-width: 0;
}

.dish-form__group {
  border-radius: 6px;
  background: rgba(0, 0, 0, 0.04);
}

.theme--dark .dish-form__group {
  background: rgba(255, 255, 255, 0.06);
}
</style>
